import { useEffect, useRef, useState } from "react";
import { Volume2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { speak } from "@/lib/tts";
import { shuffle, randomFrom } from "@/lib/gameData";
import { RHYME_ITEMS } from "@/lib/rhymeData";
import { GAME_FEEDBACK_MS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function newRound(current) {
  let pick = randomFrom(RHYME_ITEMS);
  let guard = 5;
  while (current && pick.target === current.target && guard-- > 0) {
    pick = randomFrom(RHYME_ITEMS);
  }
  const options = shuffle([pick.correct, ...pick.distractors]);
  return { item: pick, options };
}

export default function RhymeHunt() {
  const [round, setRound] = useState(() => newRound(null));
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [locked, setLocked] = useState(false);
  const timersRef = useRef([]);
  const { award } = useAuth();

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };
  useEffect(() => () => clearTimers(), []);

  useEffect(() => {
    setFeedback(null);
    setLocked(false);
    const t = setTimeout(() => speak(round.item.target, { rate: 0.9 }), 200);
    timersRef.current.push(t);
  }, [round]);

  const pick = (word) => {
    if (locked) return;
    setLocked(true);
    setAttempts((a) => a + 1);
    if (word === round.item.correct) {
      setScore((s) => s + 1);
      award(10, "rhyme");
      setFeedback({ correct: true, word });
      speak(`${round.item.target}. ${word}. They rhyme!`, { rate: 0.9 });
      toast.success(`Yes! ${round.item.target} and ${word} rhyme.`);
      const t = setTimeout(() => setRound((cur) => newRound(cur.item)), GAME_FEEDBACK_MS + 400);
      timersRef.current.push(t);
    } else {
      setFeedback({ correct: false, word });
      toast.error(`Try again — that doesn't rhyme with ${round.item.target}.`);
      const t = setTimeout(() => {
        setFeedback(null);
        setLocked(false);
      }, GAME_FEEDBACK_MS);
      timersRef.current.push(t);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div
          data-testid="rhyme-score"
          className="inline-flex items-center gap-2 bg-lime-50 text-lime-700 px-4 py-2 rounded-full font-bold text-sm"
        >
          Score: {score} / {attempts}
        </div>
        <button
          data-testid="rhyme-next"
          onClick={() => setRound((cur) => newRound(cur.item))}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New word
        </button>
      </div>

      <div className="text-center bg-slate-50 rounded-3xl p-8 mb-6">
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-2">
          Which word rhymes with
        </p>
        <button
          data-testid="rhyme-target"
          onClick={() => speak(round.item.target, { rate: 0.9 })}
          className="mt-1 font-display text-6xl sm:text-7xl font-semibold text-lime-600 leading-none inline-flex items-center gap-3"
        >
          {round.item.target}
          <Volume2 className="w-8 h-8 text-lime-400" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {round.options.map((word, i) => {
          const isRight = feedback && feedback.correct && feedback.word === word;
          const isWrong = feedback && !feedback.correct && feedback.word === word;
          return (
            <button
              key={word}
              data-testid={`rhyme-option-${i}`}
              onClick={() => pick(word)}
              disabled={locked}
              className={`rounded-3xl border-2 py-5 sm:py-6 font-display text-3xl sm:text-4xl font-semibold transition-all
                ${isRight ? "bg-emerald-500 border-emerald-600 text-white phonx-letter-pop" : ""}
                ${isWrong ? "bg-rose-100 border-rose-300 text-rose-600 phonx-shake" : ""}
                ${!feedback ? "bg-white border-slate-200 text-slate-800 hover:border-lime-400 hover:text-lime-600 hover:-translate-y-1 shadow-sm hover:shadow-lg" : ""}
              `}
            >
              {word}
            </button>
          );
        })}
      </div>
    </div>
  );
}
